import React, { useContext, useEffect, useState } from "react";
import { CheckboxProps, Modal, TextAreaProps } from "semantic-ui-react";
import ModalCenterTemplate from "../../../../../../App/views/ModalCenterTemplate";
import ButtonCustom from "../../../../../../../components/Button";
import InputCustom from "../../../../../../../components/Input";
import TextAreaCustom from "../../../../../../../components/Input/TextArea";
import CheckboxCustom from "../../../../../../../components/Checkbox";
import Label from "../../../../../../../components/Label";
import { PUBLIC_URL } from "../../../../../../App/configs/Constants";
import { LocalizationContext } from "../../../../../../../locales/Translation";
import {
  toastError,
  toastSuccess,
} from "../../../../../../../components/Toast";
import { SWAL_CONFIRM_OPTION } from "../../../../../../../components/SweetAlert/configs/SwalConfigs";
import { SweetAlertCommon } from "../../../../../../../components/SweetAlert";
import {
  getRestrictionErrorMessage,
  saveUserPropertyRestrictionService,
  UserPropertyRestriction,
} from "../../../../../services/UserPropertyRestrictionServices";
import DropdownUser, { UserOption } from "./DropdownUser";

const EMPTY_USER: UserOption = { id: "", text: "" };

type Props = {
  open: boolean;
  restriction: UserPropertyRestriction | null;
  propertyId: string;
  onClose: () => void;
  onSaved: () => void;
};

const ModalBlockUser = ({
  open,
  restriction,
  propertyId,
  onClose,
  onSaved,
}: Props) => {
  const { translations } = useContext(LocalizationContext);
  const isEdit = !!restriction;
  const [user, setUser] = useState<UserOption>(EMPTY_USER);
  const [blockHotdesk, setBlockHotdesk] = useState(true);
  const [blockMeetingRoom, setBlockMeetingRoom] = useState(false);
  const [blockDayOffice, setBlockDayOffice] = useState(false);
  const [reason, setReason] = useState("");
  const [userError, setUserError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUser(
      restriction
        ? { id: restriction.user.id, text: restriction.user.email }
        : EMPTY_USER
    );
    setBlockHotdesk(restriction ? !!restriction.block_hotdesk : true);
    setBlockMeetingRoom(!!restriction?.block_meeting_room);
    setBlockDayOffice(!!restriction?.block_day_office);
    setReason(restriction?.reason || "");
    setUserError("");
  }, [open, restriction]);

  const noneChecked = !blockHotdesk && !blockMeetingRoom && !blockDayOffice;

  const _submit = async () => {
    try {
      setLoading(true);
      const response = await saveUserPropertyRestrictionService({
        property_id: propertyId,
        user_id: Number(user.id),
        block_hotdesk: blockHotdesk,
        block_meeting_room: blockMeetingRoom,
        block_day_office: blockDayOffice,
        reason: reason.trim(),
      });
      toastSuccess(
        response?.data === null ? "User unblocked" : "Restriction saved"
      );
      onSaved();
    } catch (error) {
      toastError(getRestrictionErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const _handleSave = () => {
    if (!isEdit) {
      if (!user.id) {
        setUserError("User is required");
        return;
      }
      if (noneChecked) return;
      _submit();
      return;
    }

    if (noneChecked) {
      const options = SWAL_CONFIRM_OPTION({
        title: "Unchecking all options will unblock this user. Continue?",
        confirmText: "Unblock",
        confirmClass: "danger",
        showCancelButton: true,
      });
      SweetAlertCommon({
        options,
        confirmCallback: _submit,
        cancelCallback: () => null,
      });
      return;
    }
    _submit();
  };

  return (
    <ModalCenterTemplate
      open={open}
      onClose={onClose}
      modalClassName={"modal-block-user"}
    >
      <Modal.Header>
        <div>{isEdit ? "Edit blocked user" : "Block user"}</div>
        <ButtonCustom
          onClick={onClose}
          className={"m-0 p-0 none-bg close-modal"}
        >
          <>
            {translations.settings.close}
            <img src={PUBLIC_URL + "/assets/icons/icon-close-grey.svg"} />
          </>
        </ButtonCustom>
      </Modal.Header>
      <Modal.Content>
        {isEdit ? (
          <InputCustom
            label={"User"}
            value={user.text}
            disabled
            name={"user"}
          />
        ) : (
          <>
            <Label label={"User"} required />
            <DropdownUser
              value={user}
              onChange={(value) => {
                setUser(value);
                setUserError("");
              }}
              touched={!!userError}
              error={userError}
            />
          </>
        )}
        <div className={"mb-16px"} />
        <Label label={"Block from"} />
        <div className={"blocked-users-options"}>
          <CheckboxCustom
            label={"Hot desk (check-in & desk booking)"}
            checked={blockHotdesk}
            onChange={(
              e: React.FormEvent<HTMLInputElement>,
              { checked }: CheckboxProps
            ) => setBlockHotdesk(!!checked)}
          />
          <CheckboxCustom
            label={"Meeting room"}
            checked={blockMeetingRoom}
            onChange={(
              e: React.FormEvent<HTMLInputElement>,
              { checked }: CheckboxProps
            ) => setBlockMeetingRoom(!!checked)}
          />
          <CheckboxCustom
            label={"Day office"}
            checked={blockDayOffice}
            onChange={(
              e: React.FormEvent<HTMLInputElement>,
              { checked }: CheckboxProps
            ) => setBlockDayOffice(!!checked)}
          />
        </div>
        <div className={"mb-16px"} />
        <TextAreaCustom
          label={"Reason"}
          placeholder={"Optional"}
          value={reason}
          onChange={(
            e: React.ChangeEvent<HTMLTextAreaElement>,
            { value }: TextAreaProps
          ) => setReason(String(value ?? ""))}
          name={"reason"}
        />
      </Modal.Content>
      <Modal.Actions>
        <ButtonCustom height={"47px"} onClick={onClose}>
          {translations.settings.cancel}
        </ButtonCustom>
        <ButtonCustom
          loading={loading}
          disabled={loading || (!isEdit && noneChecked)}
          className={"primary"}
          height={"47px"}
          onClick={_handleSave}
        >
          {translations.settings.save}
        </ButtonCustom>
      </Modal.Actions>
    </ModalCenterTemplate>
  );
};

export default ModalBlockUser;
